import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import discord from '../../assets/discord.svg';
import github from '../../assets/github.svg';
import google from '../../assets/google.svg';
import linkedin from '../../assets/linkedin.svg';
import producthunt from '../../assets/producthunt.svg';
import quora from '../../assets/quora.svg';
import reddit from '../../assets/reddit.svg';
import slack from '../../assets/slack.svg';
import whatsapp from '../../assets/whatsapp.svg';
import X from '../../assets/X.svg';
import youtube from '../../assets/youtube.svg';
import maps from '../../assets/maps.svg';
import outlook from '../../assets/outlook.svg';
import facebook from '../../assets/facebook.svg';
import instagram from '../../assets/instagram.svg';
import telegram from '../../assets/telegram.svg';
import medium from '../../assets/medium.svg';
import stackoverflow from '../../assets/stackoverflow.svg';
import angellist from '../../assets/angellist.svg';
import capterra from '../../assets/capterra.svg';
import g2 from '../../assets/g2.svg';
import trustpilot from '../../assets/trustpilot.svg';
import yelp from '../../assets/yelp.svg';
import behance from '../../assets/behance.svg';
import tiktok from '../../assets/tiktok.svg';

interface TooltipState {
  visible: boolean;
  text: string;
  position: { x: number; y: number };
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const AuthRightPanel: React.FC = () => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    text: '',
    position: { x: 0, y: 0 }
  });
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [isClicking, setIsClicking] = useState(false);
  const [shuffledIcons] = useState(() => {
    const iconArray = [
      google, youtube, slack, maps, X,
      outlook, github, linkedin, discord, reddit,
      facebook, instagram, telegram, whatsapp, medium,
      quora, stackoverflow, producthunt, angellist, capterra,
      g2, trustpilot, yelp, behance, tiktok
    ];
    return shuffleArray(iconArray);
  });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const container = document.querySelector('.custom-cursor-container');
      const grid = document.querySelector('.custom-cursor-container .grid');
      if (!container || !grid) return;

      // Check if the target is within a popup or tooltip
      const target = e.target as HTMLElement;
      const isPopup = target.closest('[class*="fixed"]') !== null;
      
      if (isPopup) {
        setCursorPosition({ x: -100, y: -100 }); // Hide cursor
        return;
      }

      const gridRect = grid.getBoundingClientRect();
      const isInsideGrid = 
        e.clientX >= gridRect.left &&
        e.clientX <= gridRect.right &&
        e.clientY >= gridRect.top &&
        e.clientY <= gridRect.bottom;

      if (isInsideGrid) {
        setCursorPosition({ x: e.clientX, y: e.clientY });
        
        if (tooltip.visible) {
          setTooltip(prev => ({
            ...prev,
            position: {
              x: e.clientX + 10,
              y: e.clientY + 45
            }
          }));
        }
      } else {
        setCursorPosition({ x: -100, y: -100 }); // Move cursor out of view
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      const grid = document.querySelector('.custom-cursor-container .grid');
      if (!grid) return;

      // Check if the target is within a popup or tooltip
      const target = e.target as HTMLElement;
      const isPopup = target.closest('[class*="fixed"]') !== null;
      
      if (isPopup) {
        return;
      }

      const gridRect = grid.getBoundingClientRect();
      const isInsideGrid = 
        e.clientX >= gridRect.left &&
        e.clientX <= gridRect.right &&
        e.clientY >= gridRect.top &&
        e.clientY <= gridRect.bottom;

      if (isInsideGrid) {
        setIsClicking(true);
      }
    };

    const handleMouseUp = () => setIsClicking(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [tooltip.visible]);

  // Wave animation for the container
  const containerVariants = {
    animate: { 
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  // Individual tile animations with spring physics
  const tileVariants = {
    initial: { 
      scale: 0.8,
      y: 20
    },
    animate: { 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 17
      }
    }
  };

  // Floating animation for continuous wave effect
  const floatingVariants = {
    animate: (custom: { delay: number }) => ({
      y: [0, -10, 0],
      transition: {
        duration: 2,
        delay: custom.delay,
        repeat: Infinity,
        repeatType: "reverse" as const,
        ease: "easeInOut"
      }
    })
  };

  // Function to determine if a tile should have a social icon
  const isSocialIcon = (rowIndex: number, colIndex: number) => {
    const iconPositions = {
      '0-0': true, // AWS
      '0-1': true, // Azure
      '0-2': true, // GCP
      '0-3': true, // Databricks
      '0-4': true, // whatsapp
      '1-0': true, // MongoDB
      '1-1': true, // ProductHunt
      '1-2': true, // GitHub
      '1-3': true, // LinkedIn
      '1-4': true, // Stripe
      '2-0': true, // HubSpot
      '2-1': true, // X
      '2-2': true, // Reddit
      '2-3': true, // Slack
      '2-4': true, // Salesforce
      '3-0': true, // Zendesk
      '3-1': true, // Google
      '3-2': true, // Discord
      '3-3': true, // Apple
      '3-4': true, // OpenAI
      '4-0': true, // Anthropic
      '4-1': true, // Notion
      '4-2': true, // Microsoft
      '4-3': true, // Adobe
      '4-4': true  // Figma
    };
    const key = `${rowIndex}-${colIndex}`;
    return iconPositions[key as keyof typeof iconPositions] || false;
  };

  // Function to get the correct icon for a position
  const getIconForPosition = (rowIndex: number, colIndex: number) => {
    const index = rowIndex * 5 + colIndex;
    return shuffledIcons[index];
  };

  const handleTileMouseEnter = (rowIndex: number, colIndex: number) => {
    const tooltipText = getTooltipText(rowIndex, colIndex);
    if (tooltipText) {
      setTooltip({
        visible: true,
        text: tooltipText,
        position: { x: 0, y: 0 }
      });
    }
  };

  const handleTileMouseLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const getTooltipText = (rowIndex: number, colIndex: number) => {
    const iconToTooltip: { [key: string]: string } = {
      [google]: 'Google',
      [youtube]: 'YouTube',
      [slack]: 'Slack',
      [maps]: 'Maps',
      [X]: 'X',
      [outlook]: 'Outlook',
      [github]: 'GitHub',
      [linkedin]: 'LinkedIn',
      [discord]: 'Discord',
      [reddit]: 'Reddit',
      [facebook]: 'Facebook',
      [instagram]: 'Instagram',
      [telegram]: 'Telegram',
      [whatsapp]: 'WhatsApp',
      [medium]: 'Medium',
      [quora]: 'Quora',
      [stackoverflow]: 'Stack Overflow',
      [producthunt]: 'Product Hunt',
      [angellist]: 'AngelList',
      [capterra]: 'Capterra',
      [g2]: 'G2',
      [trustpilot]: 'Trustpilot',
      [yelp]: 'Yelp',
      [behance]: 'behance',
      [tiktok]: 'tiktok'
    };
    
    const icon = getIconForPosition(rowIndex, colIndex);
    return iconToTooltip[icon];
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center bg-[#FAFAFA] overflow-hidden custom-cursor-container">
      <div 
        className={`custom-cursor ${isClicking ? 'clicking' : ''}`}
        style={{
          left: `${cursorPosition.x}px`,
          top: `${cursorPosition.y}px`,
          transform: `translate(-50%, -50%) ${isClicking ? 'scale(0.8)' : 'scale(1)'}`,
        }}
      />
      
      <motion.div 
        className="grid grid-cols-5 grid-rows-5 gap-1 relative z-10"
        variants={containerVariants}
        animate="animate"
      >
        {[...Array(5)].map((_, rowIndex) => (
          [...Array(5)].map((_, colIndex) => {
            const hasSocialIcon = isSocialIcon(rowIndex, colIndex);
            const delay = (rowIndex * 5 + colIndex) * 0.1;
            
            return (
              <motion.div
                key={`${rowIndex}-${colIndex}`}
                className="relative w-[125px] h-[126px] bg-[#FFFFFF] rounded-[28px] shadow-[0px_8px_28px_-6px_#1C28401F,0px_18px_88px_-4px_#1C284024] border-b border-[#EEEEEE] transition-all"
                variants={tileVariants}
                whileHover={{ 
                  scale: 1.05,
                  opacity: 1,
                  transition: { duration: 0.2 }
                }}
                style={{
                  opacity: Math.max(0.15, 1 - (Math.sqrt(Math.pow(rowIndex - 2, 2) + Math.pow(colIndex - 2, 2)) * 0.35))
                }}
                onMouseEnter={() => handleTileMouseEnter(rowIndex, colIndex)}
                onMouseLeave={handleTileMouseLeave}
              >
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  variants={floatingVariants}
                  custom={{ delay }}
                  animate="animate"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ 
                      scale: 1,
                      transition: {
                        type: "spring",
                        stiffness: 400,
                        damping: 17,
                        delay: delay + 0.2
                      }
                    }}
                  >
                    {hasSocialIcon && (
                      <img 
                        src={getIconForPosition(rowIndex, colIndex)}
                        alt="social icon"
                        className="w-[45px] h-[45px] object-contain"
                      />
                    )}
                  </motion.div>
                </motion.div>
              </motion.div>
            );
          })
        ))}
      </motion.div>

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className="fixed z-50 pointer-events-none animate-fadeIn"
          style={{
            left: `${tooltip.position.x}px`,
            top: `${tooltip.position.y}px`,
            transform: 'translate(0, -100%)'
          }}
        >
          <div className="bg-[#F0F0F0] text-[#222222] px-3 py-2 rounded-[40px] text-sm font-normal shadow-lg">
            {tooltip.text}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthRightPanel; 